/**
 * Front-end entry point.
 *
 * The order below is the entire front-end contract, and it is:
 *
 *   1. parse the current page view into a touch          -- in memory, no writes
 *   2. ask the consent gate                              -- reads cookies only
 *   3. if not granted, HOLD the touch in a variable      -- and do nothing else at all
 *   4. on grant (now or later this page view), replay    -- suppression decides if we call
 *   5. populate forms from the response or the snapshot  -- fallback only; the server wins
 *
 * Nothing writes to the browser before step 4. Not a cookie, not sessionStorage, not a
 * request. Step 3 is what stops a GDPR banner destroying first-touch data: the landing page
 * carrying the UTMs is exactly the page where consent is still undecided, and holding the
 * parsed touch in memory costs nothing and is not storage.
 */

import { createRules } from './rules.js';
import { classify, signalsFromUrl } from './classifier.js';
import { verdict, ACCEPTED } from './validator.js';
import { collectAnalyticsIds } from './analytics-ids.js';
import { createConsentGate } from './consent.js';
import { createSessionStore, decide } from './suppression.js';
import { bindFormRender, populateAllForms } from './forms.js';

/**
 * Reads the configuration PHP localised onto the page.
 *
 * @param {object} win Window.
 * @return {object} Configuration.
 */
function readConfig( win ) {
	const config = win.ccAttrConfig || {};

	return {
		endpoint: config.endpoint || '',
		consent: config.consent || {},
		internalHosts: Array.isArray( config.internalHosts ) ? config.internalHosts : [],
		rules: config.rules || {},
		debug: Boolean( config.debug ),
	};
}

/**
 * Sends a touch to the REST endpoint.
 *
 * Failure is silent by design. This runs on every landing page on a 500k-visit site, and a
 * console full of network errors during an outage helps nobody -- the submission path still
 * has the server-side record, and the form still has its client-stamped fallback values.
 *
 * @param {object} options Options.
 * @return {Promise<object|null>} The response body, or null.
 */
async function sendTouch( { endpoint, signals, analyticsIds, win } ) {
	if ( ! endpoint ) {
		return null;
	}

	try {
		const response = await win.fetch( endpoint, {
			method: 'POST',
			credentials: 'same-origin',
			headers: { 'Content-Type': 'application/json' },
			// Keeps the request alive across the navigation that a form submit causes.
			keepalive: true,
			body: JSON.stringify( {
				url: signals.landing,
				referrer: signals.referrer,
				...analyticsIds,
			} ),
		} );

		if ( ! response.ok ) {
			return null;
		}

		return await response.json();
	} catch {
		return null;
	}
}

/**
 * Boots the capture script.
 *
 * @param {object} [environment] Injectable environment, for tests.
 * @return {object} The running instance.
 */
export function boot( environment = {} ) {
	const win = environment.win || window;
	const doc = environment.doc || document;

	const config = readConfig( win );
	const rules = createRules( { internalHosts: config.internalHosts, overrides: config.rules } );

	const signals = signalsFromUrl( win.location ? win.location.href : '', doc.referrer || '' );
	const classification = classify( signals, rules );

	const store = createSessionStore( environment.storage || safeSessionStorage( win ) );

	const gate = createConsentGate( {
		config: config.consent,
		doc,
		win,
		getCookies: () => doc.cookie || '',
	} );

	/**
	 * The values forms are populated from. Starts as the client's own classification and
	 * is replaced by the server's answer as soon as one arrives.
	 *
	 * @type {object|null}
	 */
	let values = null;

	/**
	 * Populates from the best data currently available.
	 *
	 * @return {object|null} The values used.
	 */
	function currentValues() {
		return values || store.readSnapshot();
	}

	bindFormRender( { doc, win, getValues: currentValues } );

	/**
	 * Runs once consent is granted. Everything that writes lives inside here.
	 *
	 * @return {Promise<void>} Resolves when the touch round trip finishes.
	 */
	async function onGranted() {
		const decision = decide( { signals, rules, cookies: doc.cookie || '', store } );

		// The session marker is a browser write and only happens after consent.
		store.markSession();

		if ( ! decision.call ) {
			// Suppressed: populate from the cached snapshot and make no request at all.
			const snapshot = store.readSnapshot();

			if ( snapshot ) {
				values = snapshot;
				populateAllForms( values, doc );
			}

			return;
		}

		const body = await sendTouch( {
			endpoint: config.endpoint,
			signals,
			analyticsIds: collectAnalyticsIds( doc.cookie || '' ),
			win,
		} );

		if ( body && body.attribution ) {
			values = body.attribution;
			store.writeSnapshot( values );
		} else if ( verdict( classification, signals, rules ) === ACCEPTED ) {
			/*
			 * The request failed or was refused. Fall back to the client's own
			 * classification so the form carries something -- the server's conditional
			 * overwrite will replace whatever it actually holds at submission time, and
			 * leave the rest alone.
			 */
			values = clientFallback( classification );
		}

		if ( values ) {
			populateAllForms( values, doc );
		}
	}

	gate.listen();
	gate.onGranted( () => {
		void onGranted();
	} );

	if ( ! gate.isGranted() ) {
		/*
		 * Undecided. HOLD THE TOUCH IN MEMORY ONLY -- never a cookie, never
		 * sessionStorage, never sent. If consent arrives during this page view the gate
		 * replays it; if the page unloads first it is gone, and that is accepted rather
		 * than worked around.
		 */
		gate.hold( { signals, classification } );
	}

	return { gate, store, rules, currentValues };
}

/**
 * Maps a client-side classification onto the canonical field names.
 *
 * @param {object} classification Classification result.
 * @return {object} Canonical values.
 */
function clientFallback( classification ) {
	return {
		cc_channel: classification.channel,
		cc_last_source: classification.source,
		cc_last_medium: classification.medium,
		cc_last_campaign: classification.campaign,
		cc_last_content: classification.content,
		cc_last_term: classification.term,
		cc_click_id: classification.clickId,
		cc_click_type: classification.clickType,
	};
}

/**
 * sessionStorage, or undefined where touching it throws.
 *
 * Safari private mode and storage-blocking configurations throw on ACCESS, not on use, so
 * even reading window.sessionStorage has to be guarded.
 *
 * @param {object} win Window.
 * @return {object|undefined} Storage.
 */
function safeSessionStorage( win ) {
	try {
		return win.sessionStorage;
	} catch {
		return undefined;
	}
}

// Auto-boot in a browser, but not when imported by a test.
if ( typeof window !== 'undefined' && typeof document !== 'undefined' && ! window.ccAttrNoAutoBoot ) {
	boot();
}
